export const RADIUS_STRAIGHT_LINE_MODE = "straight_line" as const;
export const RADIUS_DISTANCE_UNITS = "km" as const;
export const RADIUS_STRAIGHT_LINE_DISCLAIMER =
  "Estimated straight-line distance from selected address coordinates." as const;

const EARTH_MEAN_RADIUS_KM = 6371.0088;

export type Coordinates = {
  lat: number;
  lon: number;
};

export type RadiusOrigin = {
  id: string;
  label: string;
  coordinates: Coordinates;
};

export type RadiusRule = {
  id: string;
  label: string;
  radiusKm: number;
  fee?: number | null;
  message?: string;
  priority?: number;
  displayOrder?: number;
};

export type RadiusNearestOrigin = {
  origin: RadiusOrigin;
  distanceKm: number;
};

export type RadiusRuleClassification = {
  eligibleRules: RadiusRule[];
  bestRule: RadiusRule | null;
  eligible: boolean;
};

export type RadiusCheckInput = {
  destination: Coordinates;
  origins: RadiusOrigin[];
  rules: RadiusRule[];
};

export type RadiusCheckResult = {
  mode: typeof RADIUS_STRAIGHT_LINE_MODE;
  units: typeof RADIUS_DISTANCE_UNITS;
  distanceKm: number | null;
  nearestOrigin: RadiusNearestOrigin | null;
  eligibleRules: RadiusRule[];
  bestRule?: RadiusRule;
  eligible: boolean;
  message: string;
  disclaimer: typeof RADIUS_STRAIGHT_LINE_DISCLAIMER;
};

type IndexedRadiusRule = {
  rule: RadiusRule;
  index: number;
};

export function toRadians(degrees: number): number {
  return (degrees * Math.PI) / 180;
}

export function validateCoordinates(coords: Coordinates): boolean {
  return (
    Number.isFinite(coords.lat) &&
    Number.isFinite(coords.lon) &&
    coords.lat >= -90 &&
    coords.lat <= 90 &&
    coords.lon >= -180 &&
    coords.lon <= 180
  );
}

export function haversineDistanceKm(a: Coordinates, b: Coordinates): number {
  assertValidCoordinates(a, "a");
  assertValidCoordinates(b, "b");

  const fromLat = toRadians(a.lat);
  const toLat = toRadians(b.lat);
  const deltaLat = toRadians(b.lat - a.lat);
  const deltaLon = toRadians(b.lon - a.lon);

  const sinDeltaLat = Math.sin(deltaLat / 2);
  const sinDeltaLon = Math.sin(deltaLon / 2);
  const haversine =
    sinDeltaLat * sinDeltaLat +
    Math.cos(fromLat) * Math.cos(toLat) * sinDeltaLon * sinDeltaLon;
  const clampedHaversine = Math.min(1, Math.max(0, haversine));
  const angularDistance =
    2 * Math.atan2(Math.sqrt(clampedHaversine), Math.sqrt(1 - clampedHaversine));

  return EARTH_MEAN_RADIUS_KM * angularDistance;
}

export function findNearestOrigin(
  destination: Coordinates,
  origins: RadiusOrigin[]
): RadiusNearestOrigin | null {
  assertValidCoordinates(destination, "destination");

  let nearest: RadiusNearestOrigin | null = null;

  for (const origin of origins) {
    if (!validateCoordinates(origin.coordinates)) {
      continue;
    }

    const distanceKm = haversineDistanceKm(destination, origin.coordinates);

    if (nearest === null || distanceKm < nearest.distanceKm) {
      nearest = {
        origin,
        distanceKm
      };
    }
  }

  return nearest;
}

export function classifyRadiusRules(
  distanceKm: number,
  rules: RadiusRule[]
): RadiusRuleClassification {
  if (!Number.isFinite(distanceKm) || distanceKm < 0) {
    return {
      eligibleRules: [],
      bestRule: null,
      eligible: false
    };
  }

  const eligibleRules = rules
    .map((rule, index): IndexedRadiusRule => ({ rule, index }))
    .filter(({ rule }) => isValidRadiusKm(rule.radiusKm) && distanceKm <= rule.radiusKm)
    .sort(compareRadiusRules)
    .map(({ rule }) => rule);

  return {
    eligibleRules,
    bestRule: eligibleRules[0] ?? null,
    eligible: eligibleRules.length > 0
  };
}

export function checkRadius(input: RadiusCheckInput): RadiusCheckResult {
  if (!validateCoordinates(input.destination)) {
    return buildRadiusResult("Selected address coordinates are missing or invalid.");
  }

  const nearestOrigin = findNearestOrigin(input.destination, input.origins);

  if (nearestOrigin === null) {
    return buildRadiusResult(
      input.origins.length === 0
        ? "No radius origins are configured."
        : "No valid radius origins are configured."
    );
  }

  const classification = classifyRadiusRules(nearestOrigin.distanceKm, input.rules);
  const noRulesConfigured = input.rules.length === 0;
  const message = buildRadiusMessage(classification.bestRule, noRulesConfigured);
  const baseResult = buildRadiusResult(
    message,
    nearestOrigin.distanceKm,
    nearestOrigin,
    classification.eligibleRules,
    classification.eligible
  );

  if (classification.bestRule === null) {
    return baseResult;
  }

  return {
    ...baseResult,
    bestRule: classification.bestRule
  };
}

function assertValidCoordinates(coords: Coordinates, label: string): void {
  if (!validateCoordinates(coords)) {
    throw new RangeError(`${label} must include finite WGS84 latitude and longitude values.`);
  }
}

function isValidRadiusKm(radiusKm: number): boolean {
  return Number.isFinite(radiusKm) && radiusKm >= 0;
}

function compareRadiusRules(a: IndexedRadiusRule, b: IndexedRadiusRule): number {
  const priorityDifference = numericRuleOrder(a.rule.priority, b.rule.priority, 0);

  if (priorityDifference !== 0) {
    return priorityDifference;
  }

  const displayOrderDifference = numericRuleOrder(
    a.rule.displayOrder,
    b.rule.displayOrder,
    Number.POSITIVE_INFINITY
  );

  if (displayOrderDifference !== 0) {
    return displayOrderDifference;
  }

  const radiusDifference = a.rule.radiusKm - b.rule.radiusKm;

  if (radiusDifference !== 0) {
    return radiusDifference;
  }

  return a.index - b.index;
}

function numericRuleOrder(
  a: number | undefined,
  b: number | undefined,
  fallback: number
): number {
  const left = typeof a === "number" && Number.isFinite(a) ? a : fallback;
  const right = typeof b === "number" && Number.isFinite(b) ? b : fallback;

  return left - right;
}

function buildRadiusMessage(bestRule: RadiusRule | null, noRulesConfigured: boolean): string {
  const ruleMessage = bestRule?.message?.trim();

  if (ruleMessage) {
    return ruleMessage;
  }

  if (bestRule !== null) {
    return `${bestRule.label} applies within ${formatRadiusKm(bestRule.radiusKm)} km straight-line radius.`;
  }

  if (noRulesConfigured) {
    return "No radius rules are configured.";
  }

  return "Selected address is outside the configured straight-line radius rules.";
}

function formatRadiusKm(radiusKm: number): string {
  if (Number.isInteger(radiusKm)) {
    return String(radiusKm);
  }

  return String(Number(radiusKm.toFixed(3)));
}

function buildRadiusResult(
  message: string,
  distanceKm: number | null = null,
  nearestOrigin: RadiusNearestOrigin | null = null,
  eligibleRules: RadiusRule[] = [],
  eligible = false
): RadiusCheckResult {
  return {
    mode: RADIUS_STRAIGHT_LINE_MODE,
    units: RADIUS_DISTANCE_UNITS,
    distanceKm,
    nearestOrigin,
    eligibleRules,
    eligible,
    message,
    disclaimer: RADIUS_STRAIGHT_LINE_DISCLAIMER
  };
}
